/**
 * Common Styles for Subpages
 * Shared styling patterns used across Industry Metrics, Benchmarks, and Security Analysis pages
 */

import { WORKSPACE_CONTENT_MAX_WIDTH } from '../components/common/AnalyticsWorkspace/workspaceStyles';

// ============================================
// CONTAINER STYLES
// ============================================

/**
 * Main page container - full height flex column layout
 */
export const pageContainerSx = {
  height: '100%',
  marginBottom: '0px',
  display: 'flex',
  flexDirection: 'column',
};

/**
 * Loading spinner container - centered
 */
export const loadingContainerSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
};

/**
 * Content wrapper - flex container for main content
 */
export const contentContainerSx = {
  flex: 1,
  width: '100%',
  maxWidth: WORKSPACE_CONTENT_MAX_WIDTH,
  mx: 'auto',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

/**
 * Section container - centered flex column layout for filter sections
 * Used to wrap filter controls with their sublabels
 */
export const sectionContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 0.25,
};

/**
 * Filter sublabel - small uppercase label below filter controls
 * @param {Object} theme - MUI theme object
 * @returns {Object} MUI sx object
 */
export const filterSublabelSx = (theme) => ({
  fontSize: '10px',
  color: theme.palette.grey[500],
  fontWeight: 400,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  pt: 0.5,
  textAlign: 'center',
});

// ============================================
// HEADER AND FILTER BAR STYLES
// ============================================

/**
 * Header container with theme-aware shadow
 * @param {Object} theme - MUI theme object
 * @returns {Object} MUI sx object
 */
export const headerContainerSx = (theme) => ({
  backgroundColor: theme.background.backgroundColor,
  marginBottom: '12px',
  borderTopLeftRadius: '0px',
  borderTopRightRadius: '0px',
  boxShadow: theme.palette.mode === 'light'
    ? '0px 1px 1px -1px rgba(0,0,0,0.06), 0px 1px 1px 0px rgba(0,0,0,0.03), 0px 3px 3px 0px rgba(0,0,0,0.1)'
    : '0px 1px 1px -1px rgba(0,0,0,0.4), 0px 1px 1px 0px rgba(0,0,0,0.1), 0px 3px 3px 0px rgba(0,0,0,0.3)',
});

/**
 * Collapse container
 */
export const collapseContainerSx = {
  width: '100%',
};

/**
 * Filter bar outer wrapper
 */
export const filterBarOuterSx = {
  position: 'relative',
  marginTop: '0px',
  paddingLeft: '0%',
  marginLeft: '0px',
};

/**
 * Filter bar inner wrapper
 */
export const filterBarInnerSx = {
  paddingBottom: '4px',
  maxWidth: '100%',
  paddingTop: '2px',
  marginTop: '0px',
  marginBottom: '0px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
};

/**
 * Filter items flex container
 * @param {number} maxWidth - Maximum width in pixels (e.g., 1050, 1350, 1500)
 * @returns {Object} MUI sx object
 */
export const filterFlexContainerSx = (maxWidth = 1350) => ({
  display: 'flex',
  width: `${maxWidth}px`,
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
});

/**
 * Filter content wrapper
 * @param {Object} theme - MUI theme object
 * @param {number} maxWidth - Maximum width in pixels
 * @returns {Object} MUI sx object
 */
export const filterContentWrapperSx = (theme, maxWidth = 1800) => ({
  border: '0px solid lightgrey',
  width: '100%',
  maxWidth: `${maxWidth}px`,
  marginLeft: '4px',
  paddingTop: '4px',
  marginTop: '0px',
  paddingLeft: '4px',
  paddingRight: '4px',
  paddingBottom: '0px',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
});

/**
 * Filter label text - always wraps to save horizontal space
 * @param {boolean} isMobile - Is mobile viewport
 * @returns {Object} MUI sx object
 */
export const filterLabelSx = (isMobile = false) => ({
  fontSize: '11px',
  fontWeight: 'normal',
  textAlign: 'left',
  paddingTop: '0px',
  paddingRight: '8px',
  paddingLeft: '4px',
  color: (theme) => theme.palette.grey[600],
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'center',
  lineHeight: '1.3',
  whiteSpace: 'normal',
  maxWidth: '75px',
});

/**
 * Individual filter item wrapper - consistent height and spacing
 */
export const filterItemSx = {
  marginTop: '0px',
  marginBottom: '0px',
  marginLeft: '2px',
  marginRight: '2px',
  flexShrink: 1,
  display: 'flex',
  alignItems: 'flex-start',
  minHeight: '30px',
};

/**
 * Filter item with specific width
 * @param {number} width - Width in pixels
 * @returns {Object} MUI sx object
 */
export const filterItemWithWidthSx = (width) => ({
  ...filterItemSx,
  width: `${width}px`,
});

/**
 * Collapse toggle button
 */
export const collapseToggleSx = {
  p: 0.5,
  minWidth: 36,
  minHeight: 36,
  fontSize: 18,
  borderRadius: '50%',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
};

// ============================================
// TABLE/CHART TITLE AND HEADER STYLES
// ============================================

/**
 * Table/Chart title container
 * @param {number|string} tableWidth - Width of table/chart
 * @param {boolean} isMobile - Is mobile viewport
 * @param {Object} theme - MUI theme object
 * @returns {Object} MUI sx object
 */
export const tableTitleContainerSx = (tableWidth, isMobile = false, theme) => ({
  flex: 1,
  display: 'flex',
  justifyContent: 'space-between',
  paddingBottom: '4px',
  width: tableWidth,
  maxWidth: isMobile ? '98%' : '95%',
  color: theme.palette.primary.main,
  fontSize: '20px',
  fontWeight: '300',
  textAlign: 'left',
  paddingLeft: '0.5%',
});

/**
 * Methodology/documentation link wrapper
 * @param {Object} theme - MUI theme object
 * @returns {Object} MUI sx object
 */
export const methodologyLinkSx = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.primary.main,
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 400,
  marginTop: '-5px',
  marginLeft: 1,
});

/**
 * Methodology link text
 * @param {Object} theme - MUI theme object
 * @returns {Object} MUI sx object
 */
export const methodologyLinkTextSx = (theme) => ({
  fontWeight: 'light',
  fontStyle: 'italic',
  fontSize: '10px',
  color: theme.palette.text.secondary,
});

/**
 * Arrow icon for methodology link
 * @param {Object} theme - MUI theme object
 * @returns {Object} MUI sx object
 */
export const methodologyArrowSx = (theme) => ({
  color: theme.palette.text.secondary,
  marginLeft: '2px',
  fontSize: '10px',
  verticalAlign: 'middle',
});

// ============================================
// CHART/TABLE WRAPPER STYLES
// ============================================

/**
 * Chart/Table outer wrapper
 * @param {number|string} tableWidth - Width of table/chart
 * @param {string} maxWidth - Max width percentage (default '95%')
 * @returns {Object} MUI sx object
 */
export const chartTableWrapperSx = (tableWidth, maxWidth = '95%') => ({
  display: 'flex',
  height: '100%',
  maxWidth,
  width: tableWidth,
});

/**
 * Chart/Table inner positioning wrapper (relative)
 */
export const chartTableInnerSx = {
  position: 'relative',
  flex: 1,
};

/**
 * Chart/Table absolute positioning wrapper
 */
export const chartTableAbsoluteSx = {
  position: 'absolute',
  width: '100%',
  height: '98%',
};

/**
 * Logo watermark overlay
 * @param {number} top - Top position
 * @param {number} right - Right position (optional)
 * @param {number} left - Left position (optional)
 * @param {number} bottom - Bottom position (optional)
 * @returns {Object} Style object
 */
export const logoWatermarkStyle = ({ top, right, left, bottom }) => ({
  position: 'absolute',
  ...(top !== undefined && { top }),
  ...(right !== undefined && { right }),
  ...(left !== undefined && { left }),
  ...(bottom !== undefined && { bottom }),
  width: '130px',
  height: 'auto',
  opacity: 0.2,
});

// ============================================
// TOGGLE/SWITCH STYLES
// ============================================

/**
 * Toggle/Switch container
 * @param {Object} theme - MUI theme object
 * @param {number} width - Width in pixels (default 125)
 * @returns {Object} MUI sx object
 */
export const toggleContainerSx = (theme, width = 125) => ({
  height: '30px',
  display: 'flex',
  flexDirection: 'row',
  minWidth: `${width}px`,
});

/**
 * Toggle box wrapper
 * @param {Object} theme - MUI theme object
 * @param {number} width - Width in pixels (default 125)
 * @returns {Object} MUI sx object
 */
export const toggleBoxSx = (theme, width = 125) => ({
  p: '2px',
  backgroundColor: theme.tableHeader.highlight,
  width: `${width}px`,
  display: 'flex',
  alignItems: 'center',
  border: theme.palette.mode === 'light'
    ? `1px solid ${theme.palette.grey[400]}`
    : `1px solid ${theme.palette.grey[700]}`,
  borderRadius: '4px',
});

/**
 * Toggle label
 * @param {Object} theme - MUI theme object
 * @returns {Object} MUI sx object
 */
export const toggleLabelSx = (theme) => ({
  borderLeft: `1px solid ${theme.palette.divider}`,
  pl: '6px',
  color: theme.palette.text.secondary,
  fontSize: '9px',
});

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Calculate table width based on column definitions
 * @param {Array} colDefs - Column definitions
 * @param {number} minWidth - Minimum width (default 500)
 * @returns {number} Calculated width
 */
export const calculateTableWidth = (colDefs, minWidth = 500) => {
  const calculatedWidth = colDefs.reduce((sum, colDef) => sum + (colDef.width || 100), 0) + 5;
  return Math.max(calculatedWidth, minWidth);
};

/**
 * Calculate table height based on row count
 * @param {number} rowCount - Number of rows
 * @param {number} rowHeight - Height per row (default 30)
 * @param {number} headerOffset - Additional header height (default 15)
 * @returns {number} Calculated height
 */
export const calculateTableHeight = (rowCount, rowHeight = 30, headerOffset = 15) => {
  return (rowCount + 1) * rowHeight + headerOffset;
};

/**
 * Calculate responsive table width based on data and columns
 * @param {Array} data - Data array
 * @param {Array} columns - Column array
 * @param {number} multiplier - Width multiplier per cell (default 50)
 * @param {number} minWidth - Minimum width (default 800)
 * @returns {number} Calculated width
 */
export const calculateResponsiveWidth = (data, columns, multiplier = 50, minWidth = 800) => {
  return Math.max(data.length * columns.length * multiplier, minWidth);
};
